import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight, X, Filter, Phone, MessageCircle, ChevronDown, Award, Star, Heart, Quote } from 'lucide-react';
import { brandTheme } from '../styles/BrandTheme';
import HeroSectionComponent from '../components/HeroSection';

const HeroSection = styled.section`
  height: 100vh;
  background: 
    linear-gradient(135deg, rgba(0, 0, 0, 0.4) 0%, rgba(0, 0, 0, 0.6) 100%),
    url('/images/resin-surfaces-bound-work11.jpeg') center/cover;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  text-align: center;
  position: relative;
  overflow: hidden;
  padding: 0 1rem;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(45deg, transparent 30%, rgba(254, 206, 27, 0.1) 50%, transparent 70%);
    animation: shimmer 3s ease-in-out infinite;
  }

  @keyframes shimmer {
    0%, 100% { transform: translateX(-100%); }
    50% { transform: translateX(100%); }
  }

  /* iPad Pro and Large Tablets */
  @media (max-width: 1366px) and (min-width: 1025px) {
    height: 100vh;
    padding: 0 2rem;
  }

  /* iPad Air and Medium Tablets */
  @media (max-width: 1024px) and (min-width: 769px) {
    height: 100vh;
    padding: 0 1.5rem;
  }

  /* Landscape Mobile and Small Tablets */
  @media (max-width: 768px) and (min-width: 569px) and (orientation: landscape) {
    height: 100vh;
    padding: 0 1.25rem;
  }

  /* Portrait Mobile - iPhone 12 Pro Max and Large Mobile */
  @media (max-width: 768px) and (min-width: 428px) and (orientation: portrait) {
    height: 100vh;
    padding: 0 1.25rem;
  }

  /* iPhone 12 and Standard Mobile */
  @media (max-width: 427px) and (min-width: 390px) {
    height: 100vh;
    padding: 0 1rem;
  }

  /* iPhone SE and Small Mobile */
  @media (max-width: 389px) and (min-width: 320px) {
    height: 100vh;
    padding: 0 0.75rem;
  }

  /* Landscape orientation adjustments */
  @media (orientation: landscape) and (max-height: 500px) {
    height: 100vh;
    padding: 0 1rem;
  }
`;

const HeroContent = styled.div`
  max-width: 900px;
  margin: 0 auto;
  padding: 0 1rem;
  position: relative;
  z-index: 2;
`;

const HeroTitle = styled(motion.h1)`
  font-size: clamp(2rem, 5.5vw, 4.5rem);
  margin-bottom: 1.5rem;
  font-weight: 700;
  line-height: 1.1;
  background: linear-gradient(135deg, #FECE1B 0%, #ffffff 50%, #FECE1B 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  text-shadow: 0 4px 20px rgba(0, 0, 0, 0.5);

  /* Mobile adjustments */
  @media (max-width: 389px) {
    font-size: clamp(1.75rem, 5vw, 2.5rem);
    margin-bottom: 1rem;
    line-height: 1.2;
  }

  @media (max-width: 427px) and (min-width: 390px) {
    font-size: clamp(1.875rem, 5.25vw, 3rem);
    margin-bottom: 1.25rem;
  }

  /* Landscape mobile adjustments */
  @media (orientation: landscape) and (max-height: 500px) {
    font-size: clamp(1.5rem, 4vw, 2.5rem);
    margin-bottom: 0.75rem;
  }
`;

const HeroSubtitle = styled(motion.p)`
  font-size: clamp(1rem, 2.5vw, 1.5rem);
  margin-bottom: 2.5rem;
  opacity: 0.95;
  line-height: 1.6;
  max-width: 700px;
  margin-left: auto;
  margin-right: auto;
  color: white;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);

  /* Mobile adjustments */
  @media (max-width: 389px) {
    font-size: clamp(0.875rem, 2.25vw, 1.125rem);
    margin-bottom: 1.5rem;
    line-height: 1.5;
    max-width: 100%;
  }

  @media (max-width: 427px) and (min-width: 390px) {
    font-size: clamp(0.9375rem, 2.375vw, 1.25rem);
    margin-bottom: 2rem;
  }

  /* Landscape mobile adjustments */
  @media (orientation: landscape) and (max-height: 500px) {
    font-size: clamp(0.75rem, 2vw, 1rem);
    margin-bottom: 1rem;
    line-height: 1.4;
  }
`;

const CTAButtons = styled(motion.div)`
  display: flex;
  gap: 1.5rem;
  justify-content: center;
  flex-wrap: wrap;
  margin-bottom: 3rem;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: center;
    gap: 1rem;
  }
`;

const HeroBadge = styled(motion.div)`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  background: rgba(254, 206, 27, 0.2);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 50px;
  padding: 0.75rem 1.5rem;
  margin-bottom: 2rem;
  font-size: 0.9rem;
  font-weight: 500;
  color: white;

  svg {
    color: #FECE1B;
  }
`;

const StyledButton = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 0.75rem;
  padding: 1.25rem 2.5rem;
  border-radius: 50px;
  font-weight: 600;
  text-decoration: none;
  transition: all 0.3s ease;
  font-size: 1.1rem;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
    transition: left 0.5s;
  }

  &:hover::before {
    left: 100%;
  }

  &.primary {
    background: linear-gradient(135deg, #FECE1B 0%, #d97706 100%);
    color: white;
    box-shadow: 0 10px 30px rgba(254, 206, 27, 0.4);
    border: 2px solid transparent;

    &:hover {
      transform: translateY(-3px);
      box-shadow: 0 15px 40px rgba(254, 206, 27, 0.5);
    }
  }

  &.secondary {
    background: rgba(255, 255, 255, 0.1);
    color: white;
    border: 2px solid rgba(255, 255, 255, 0.3);
    backdrop-filter: blur(10px);

    &:hover {
      background: rgba(255, 255, 255, 0.2);
      border-color: rgba(255, 255, 255, 0.5);
      transform: translateY(-2px);
    }
  }
`;

const ScrollIndicator = styled(motion.div)`
  position: absolute;
  bottom: 2rem;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
  color: rgba(255, 255, 255, 0.7);
  font-size: 0.9rem;
  cursor: pointer;

  svg {
    animation: bounce 2s infinite;
  }

  @keyframes bounce {
    0%, 20%, 50%, 80%, 100% { transform: translateY(0); }
    40% { transform: translateY(-10px); }
    60% { transform: translateY(-5px); }
  }

  @media (max-width: 768px) {
    bottom: 1rem;
  }
`;

const Section = styled.section`
  padding: 6rem 0;
  position: relative;

  &:nth-child(even) {
    background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
  }

  @media (max-width: 768px) {
    padding: 4rem 0;
  }
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 1rem;
  position: relative;
  z-index: 1;
`;

const SectionTitle = styled.h2`
  text-align: center;
  margin-bottom: 1rem;
  font-size: clamp(2rem, 4vw, 3rem);
  font-weight: 700;
  color: ${brandTheme.colors.primary[900]};
  position: relative;

  &::after {
    content: '';
    position: absolute;
    bottom: -10px;
    left: 50%;
    transform: translateX(-50%);
    width: 60px;
    height: 4px;
    background: linear-gradient(135deg, ${brandTheme.colors.primary[500]} 0%, ${brandTheme.colors.primary[600]} 100%);
    border-radius: 2px;
  }
`;

const FilterSection = styled.div`
  display: flex;
  justify-content: center;
  gap: 1rem;
  margin-bottom: 3rem;
  flex-wrap: wrap;

  @media (max-width: 768px) {
    gap: 0.5rem;
  }
`;

const FilterButton = styled.button<{ active: boolean }>`
  padding: 0.75rem 1.5rem;
  border: 2px solid ${props => props.active ? '#FECE1B' : '#e5e7eb'};
  background: ${props => props.active ? '#FECE1B' : 'white'};
  color: ${props => props.active ? 'white' : '#666'};
  border-radius: 25px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;

  &:hover {
    border-color: #FECE1B;
    background: ${props => props.active ? '#d97706' : '#f8fafc'};
  }

  @media (max-width: 768px) {
    padding: 0.5rem 1rem;
    font-size: 0.9rem;
  }
`;

const GalleryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 2rem;
  margin: 3rem 0;
`;

const GalleryItem = styled(motion.div)`
  position: relative;
  border-radius: 15px;
  overflow: hidden;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
  cursor: pointer;
  aspect-ratio: 4/3;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: transform 0.3s ease;
  }

  &:hover img {
    transform: scale(1.05);
  }

  .overlay {
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    background: linear-gradient(transparent, rgba(0, 0, 0, 0.8));
    color: white;
    padding: 1.5rem;
    transform: translateY(100%);
    transition: transform 0.3s ease;

    h3 {
      margin-bottom: 0.5rem;
      font-size: 1.1rem;
    }

    p {
      font-size: 0.9rem;
      opacity: 0.9;
    }
  }

  &:hover .overlay {
    transform: translateY(0);
  }
`;

const Lightbox = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.9);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 2rem;

  .close-button {
    position: absolute;
    top: 2rem;
    right: 2rem;
    background: rgba(255, 255, 255, 0.2);
    border: none;
    color: white;
    width: 50px;
    height: 50px;
    border-radius: 50%;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: background 0.3s ease;

    &:hover {
      background: rgba(255, 255, 255, 0.3);
    }
  }

  .lightbox-content {
    position: relative;
    max-width: 90vw;
    max-height: 90vh;

    img {
      width: 100%;
      height: 100%;
      object-fit: contain;
      border-radius: 15px;
    }

    .lightbox-info {
      position: absolute;
      bottom: -80px;
      left: 0;
      right: 0;
      background: white;
      padding: 1.5rem;
      border-radius: 15px;
      color: #333;

      h3 {
        margin-bottom: 0.5rem;
        color: #FECE1B;
      }

      p {
        color: #666;
        margin: 0;
      }
    }
  }
`;

const BeforeAfterSection = styled.section`
  background: #f8fafc;
  padding: 5rem 0;
`;

const BeforeAfterGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(400px, 1fr));
  gap: 3rem;
  margin: 3rem 0;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 2rem;
  }
`;

const BeforeAfterCard = styled(motion.div)`
  background: white;
  border-radius: 15px;
  overflow: hidden;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);

  .images {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 0;

    img {
      width: 100%;
      height: 250px;
      object-fit: cover;
    }
  }

  .content {
    padding: 1.5rem;

    h3 {
      color: #FECE1B;
      margin-bottom: 1rem;
    }

    p {
      color: #666;
      margin-bottom: 1rem;
    }

    .location {
      color: #999;
      font-size: 0.9rem;
    }
  }
`;

const SectionSubtitle = styled.p`
  text-align: center;
  font-size: clamp(1rem, 2.5vw, 1.25rem);
  color: ${brandTheme.colors.neutral.gray[600]};
  max-width: 600px;
  margin: 0 auto 3rem auto;
  line-height: 1.6;
`;

const FAQSection = styled.section`
  background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
  padding: 6rem 0;
  position: relative;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23e2e8f0' fill-opacity='0.4'%3E%3Ccircle cx='30' cy='30' r='2'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E") repeat;
    opacity: 0.3;
  }

  @media (max-width: 768px) {
    padding: 4rem 0;
  }
`;

const FAQGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 1.5rem;
  max-width: 800px;
  margin: 0 auto;
`;

const FAQItem = styled(motion.div)`
  background: white;
  border-radius: 15px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  border: 1px solid #e2e8f0;
  overflow: hidden;
  transition: all 0.3s ease;

  &:hover {
    box-shadow: 0 8px 30px rgba(0, 0, 0, 0.12);
    transform: translateY(-2px);
  }

  .question {
    padding: 1.5rem 2rem;
    cursor: pointer;
    display: flex;
    justify-content: space-between;
    align-items: center;
    font-weight: 600;
    font-size: 1.1rem;
    color: ${brandTheme.colors.primary[900]};
    transition: all 0.3s ease;
    background: linear-gradient(135deg, #ffffff 0%, #f8fafc 100%);

    &:hover {
      background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
    }

    svg {
      transition: transform 0.3s ease;
      color: ${brandTheme.colors.primary[500]};
    }

    &.active svg {
      transform: rotate(180deg);
    }
  }

  .answer {
    padding: 0 2rem;
    max-height: 0;
    overflow: hidden;
    transition: all 0.3s ease;
    background: white;
    color: ${brandTheme.colors.neutral.gray[700]};
    line-height: 1.6;

    &.active {
      padding: 0 2rem 1.5rem 2rem;
      max-height: 200px;
    }
  }

  @media (max-width: 768px) {
    .question {
      padding: 1.25rem 1.5rem;
      font-size: 1rem;
    }

    .answer {
      &.active {
        padding: 0 1.5rem 1.25rem 1.5rem;
      }
    }
  }
`;

const CTASection = styled.section`
  background: linear-gradient(135deg, #FECE1B 0%, #d97706 100%);
  color: white;
  padding: 6rem 0;
  text-align: center;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.05'%3E%3Ccircle cx='30' cy='30' r='2'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E") repeat;
  }

  @media (max-width: 768px) {
    padding: 4rem 0;
  }
`;

const GalleryPage: React.FC = () => {
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [lightboxImage, setLightboxImage] = useState<string | null>(null);
  const [lightboxInfo, setLightboxInfo] = useState<{ title: string; description: string } | null>(null);
  const [openFAQ, setOpenFAQ] = useState<number | null>(null);

  const categories = [
    { id: 'all', label: 'All Projects', icon: <Filter size={16} /> },
    { id: 'driveways', label: 'Driveways', icon: <Filter size={16} /> },
    { id: 'pathways', label: 'Pathways', icon: <Filter size={16} /> },
    { id: 'pool-decks', label: 'Pool Decks', icon: <Filter size={16} /> }
  ];

  const galleryItems = [
    {
      id: 1,
      category: 'driveways',
      title: 'Modern Grey Driveway',
      description: 'Contemporary resin bound driveway in Richmond',
      image: '/images/resin-surfaces-bound-work1.jpeg',
      location: 'Richmond, Surrey'
    },
    {
      id: 2,
      category: 'pathways',
      title: 'Garden Pathway',
      description: 'Beautiful curved pathway through garden',
      image: '/images/resin-surfaces-bound-work2.jpeg',
      location: 'St Albans, Hertfordshire'
    },
    {
      id: 3,
      category: 'pool-decks',
      title: 'Pool Surround',
      description: 'Anti-slip pool deck installation',
      image: '/images/resin-surfaces-bound-work3.jpeg',
      location: 'Hampstead, London'
    },
    {
      id: 4,
      category: 'driveways',
      title: 'Traditional Driveway',
      description: 'Classic resin bound driveway with natural stone',
      image: '/images/resin-surfaces-bound-work4.jpeg',
      location: 'Kingston, Surrey'
    },
    {
      id: 5,
      category: 'pathways',
      title: 'Side Access Path',
      description: 'Narrow pathway for utility access',
      image: '/images/resin-surfaces-bound-work5.jpeg',
      location: 'Croydon, Surrey'
    },
    {
      id: 6,
      category: 'pool-decks',
      title: 'Modern Pool Deck',
      description: 'Contemporary pool surround design',
      image: '/images/resin-surfaces-bound-work6.jpeg',
      location: 'Watford, Hertfordshire'
    }
  ];

  const beforeAfterProjects = [
    {
      id: 1,
      title: 'Complete Driveway Transformation',
      description: 'Old cracked concrete driveway transformed into beautiful resin bound surfacing',
      before: 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80',
      after: 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80',
      location: 'Richmond, Surrey'
    },
    {
      id: 2,
      title: 'Garden Pathway Installation',
      description: 'Muddy garden path replaced with elegant resin bound pathway',
      before: 'https://images.unsplash.com/photo-1416879595882-3373a0480b5b?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80',
      after: 'https://images.unsplash.com/photo-1416879595882-3373a0480b5b?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80',
      location: 'St Albans, Hertfordshire'
    },
    {
      id: 3,
      title: 'Pool Deck Makeover',
      description: 'Dangerous slippery tiles replaced with safe resin bound pool deck',
      before: 'https://images.unsplash.com/photo-1575429198097-0414ec08e8cd?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80',
      after: 'https://images.unsplash.com/photo-1575429198097-0414ec08e8cd?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80',
      location: 'Hampstead, London'
    }
  ];

  const filteredItems = selectedCategory === 'all' 
    ? galleryItems 
    : galleryItems.filter(item => item.category === selectedCategory);

  const openLightbox = (image: string, title: string, description: string) => {
    setLightboxImage(image);
    setLightboxInfo({ title, description });
  };

  const closeLightbox = () => {
    setLightboxImage(null);
    setLightboxInfo(null);
  };

  const toggleFAQ = (index: number) => {
    setOpenFAQ(openFAQ === index ? null : index);
  };

  const faqs = [
    {
      question: 'How long does a resin bound installation take?',
      answer: 'Most residential projects take 1-3 days depending on size and complexity. Driveways typically take 1-2 days, while larger commercial projects may take 3-5 days. We provide detailed timelines during our free consultation.'
    },
    {
      question: 'Can I see examples of your work before hiring you?',
      answer: 'Absolutely! Our gallery showcases hundreds of completed projects across London, Surrey, Hertfordshire, Kent, Essex, Buckinghamshire, and Berkshire. We also provide references from previous clients and can arrange site visits to see our work in person.'
    },
    {
      question: 'Do you offer warranties on your resin bound installations?',
      answer: 'Yes, we offer comprehensive warranties on all our installations. Our resin bound surfaces come with a 10+ year warranty, and we provide full aftercare support to ensure your investment is protected.'
    },
    {
      question: 'What areas do you cover for resin bound installations?',
      answer: 'We cover all of London, Surrey, Hertfordshire, Kent, Essex, Buckinghamshire, and Berkshire. Our service areas include Richmond, Kingston, Croydon, St Albans, Watford, Canterbury, Maidstone, Chelmsford, Colchester, Aylesbury, High Wycombe, Reading, Windsor, and surrounding areas. Contact us to confirm coverage for your specific location.'
    },
    {
      question: 'Can resin bound surfaces be installed over existing driveways?',
      answer: 'Yes, in most cases we can install resin bound surfaces over existing concrete or tarmac driveways, provided they are in good structural condition. We assess the existing surface during our free survey and advise on the best approach.'
    },
    {
      question: 'How do I maintain my resin bound surface?',
      answer: 'Resin bound surfaces are very low maintenance. Regular sweeping and occasional pressure washing (once or twice a year) is usually sufficient. We provide detailed maintenance instructions and are always available for advice.'
    }
  ];

  return (
    <>
      <Helmet>
        <title>Resin Surfaces Gallery | Before & After Photos | London Projects | 1000+ Completed Projects | 5★ Rated</title>
        <meta name="description" content="🏆 Explore our stunning resin bound surfacing gallery! 1000+ completed projects across London, Surrey & Hertfordshire. Before & after photos of driveways, pathways & pool decks. 5★ rated, 10+ years experience. See the quality of our work!" />
        <meta name="keywords" content="resin surfaces gallery London, resin bound before after photos, driveway gallery London, pathway gallery Surrey, pool deck gallery Hertfordshire, resin bound projects London, completed installations gallery, Vuba Surfaces projects, GCL Products installations, 5 star rated resin surfacing" />
        <link rel="canonical" href="https://www.resinsurfaceslondon.co.uk/gallery/" />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.resinsurfaceslondon.co.uk/gallery/" />
        <meta property="og:title" content="Resin Surfaces Gallery | Before & After Photos | London Projects | 1000+ Completed Projects" />
        <meta property="og:description" content="🏆 Explore our stunning resin bound surfacing gallery! 1000+ completed projects across London, Surrey & Hertfordshire. Before & after photos of driveways, pathways & pool decks. 5★ rated, 10+ years experience." />
        <meta property="og:image" content="https://www.resinsurfaceslondon.co.uk/images/resin-surfaces-bound-work11.jpeg" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:site_name" content="Resin Surfaces London" />
        <meta property="og:locale" content="en_GB" />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:url" content="https://www.resinsurfaceslondon.co.uk/gallery/" />
        <meta name="twitter:title" content="Resin Surfaces Gallery | Before & After Photos | London Projects" />
        <meta name="twitter:description" content="🏆 Explore our stunning resin bound surfacing gallery! 1000+ completed projects across London, Surrey & Hertfordshire. 5★ rated, 10+ years experience." />
        <meta name="twitter:image" content="https://www.resinsurfaceslondon.co.uk/images/resin-surfaces-bound-work11.jpeg" />

        {/* Additional Meta Tags */}
        <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
        <meta name="googlebot" content="index, follow" />
        <meta name="author" content="Resin Surfaces London" />
        <meta name="publisher" content="Resin Surfaces London" />
        <meta name="copyright" content="Resin Surfaces London" />
        <meta name="language" content="English" />
        <meta name="revisit-after" content="7 days" />
        <meta name="distribution" content="global" />
        <meta name="rating" content="general" />
        <meta name="geo.region" content="GB" />
        <meta name="geo.placename" content="London" />
        <meta name="geo.position" content="51.5074;-0.1278" />
        <meta name="ICBM" content="51.5074, -0.1278" />

        {/* Schema.org JSON-LD */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebPage",
            "name": "Resin Surfaces Gallery | Before & After Photos | London Projects",
            "description": "Explore our stunning resin bound surfacing gallery featuring 1000+ completed projects across London, Surrey & Hertfordshire. Before & after photos of driveways, pathways & pool decks.",
            "url": "https://www.resinsurfaceslondon.co.uk/gallery/",
            "mainEntity": {
              "@type": "ImageGallery",
              "name": "Resin Bound Surfacing Project Gallery",
              "description": "Professional resin bound surfacing projects completed across London, Surrey, Hertfordshire, Kent, Essex, Buckinghamshire and Berkshire",
              "provider": {
                "@type": "Organization",
                "name": "Resin Surfaces London",
                "url": "https://www.resinsurfaceslondon.co.uk",
                "logo": "https://www.resinsurfaceslondon.co.uk/logo.png",
                "contactPoint": {
                  "@type": "ContactPoint",
                  "telephone": "+44-7123-456-789",
                  "contactType": "customer service",
                  "areaServed": ["GB-LND", "GB-SRY", "GB-HRT"],
                  "availableLanguage": "English"
                },
                "address": {
                  "@type": "PostalAddress",
                  "addressLocality": "London",
                  "addressCountry": "GB"
                },
                "sameAs": [
                  "https://www.facebook.com/resinsurfaceslondon",
                  "https://www.instagram.com/resinsurfaceslondon",
                  "https://www.linkedin.com/company/resinsurfaceslondon"
                ]
              }
            },
            "breadcrumb": {
              "@type": "BreadcrumbList",
              "itemListElement": [
                {
                  "@type": "ListItem",
                  "position": 1,
                  "name": "Home",
                  "item": "https://www.resinsurfaceslondon.co.uk/"
                },
                {
                  "@type": "ListItem",
                  "position": 2,
                  "name": "Gallery",
                  "item": "https://www.resinsurfaceslondon.co.uk/gallery/"
                }
              ]
            },
            "potentialAction": {
              "@type": "ViewAction",
              "target": "https://www.resinsurfaceslondon.co.uk/gallery/",
              "name": "View Gallery"
            }
          })}
        </script>
      </Helmet>

      <HeroSectionComponent
        title="Our Project Gallery"
        subtitle="Explore our portfolio of successful resin bound surfacing projects across London, Surrey, Hertfordshire, Kent, Essex, Buckinghamshire & Berkshire"
        badge="1000+ Completed Projects • 5★ Rated"
        backgroundImage="/images/resin-surfaces-bound-work11.jpeg"
        primaryButtonText="Get Free Quote"
        primaryButtonLink="/contact/"
        secondaryButtonText="Call Now"
        secondaryButtonLink="tel:+447535935779"
      />

      <Section>
        <Container>
          <SectionTitle>Project Portfolio</SectionTitle>
          <FilterSection>
            {categories.map((category) => (
              <FilterButton
                key={category.id}
                active={selectedCategory === category.id}
                onClick={() => setSelectedCategory(category.id)}
              >
                {category.icon}
                {category.label}
              </FilterButton>
            ))}
          </FilterSection>

          <GalleryGrid>
            {filteredItems.map((item, index) => (
              <GalleryItem
                key={item.id}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                onClick={() => openLightbox(item.image, item.title, item.description)}
              >
                <img src={item.image} alt={item.title} />
                <div className="overlay">
                  <h3>{item.title}</h3>
                  <p>{item.description}</p>
                </div>
              </GalleryItem>
            ))}
          </GalleryGrid>
        </Container>
      </Section>

      <BeforeAfterSection>
        <Container>
          <SectionTitle>Before & After Transformations</SectionTitle>
          <BeforeAfterGrid>
            {beforeAfterProjects.map((project, index) => (
              <BeforeAfterCard
                key={project.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <div className="images">
                  <img src={project.before} alt="Before" />
                  <img src={project.after} alt="After" />
                </div>
                <div className="content">
                  <h3>{project.title}</h3>
                  <p>{project.description}</p>
                  <div className="location">{project.location}</div>
                </div>
              </BeforeAfterCard>
            ))}
          </BeforeAfterGrid>
        </Container>
      </BeforeAfterSection>

      <FAQSection>
        <Container>
          <SectionTitle>Frequently Asked Questions About Our Gallery</SectionTitle>
          <SectionSubtitle>
            Get answers to common questions about our resin bound surfacing projects, installation process, and what you can expect
          </SectionSubtitle>
          <FAQGrid>
            {faqs.map((faq, index) => (
              <FAQItem
                key={index}
                className={openFAQ === index ? 'active' : ''}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <div 
                  className={`question ${openFAQ === index ? 'active' : ''}`}
                  onClick={() => toggleFAQ(index)}
                >
                  {faq.question}
                  <ChevronDown size={20} />
                </div>
                <div className={`answer ${openFAQ === index ? 'active' : ''}`}>
                  {faq.answer}
                </div>
              </FAQItem>
            ))}
          </FAQGrid>
        </Container>
      </FAQSection>

      <CTASection>
        <Container>
          <SectionTitle style={{ color: 'white' }}>Inspired by Our Work?</SectionTitle>
          <p style={{ fontSize: '1.2rem', marginBottom: '2rem', opacity: 0.9 }}>
            Let us transform your outdoor space with the same quality and attention to detail
          </p>
          <CTAButtons>
            <StyledButton to="/contact/" className="secondary">
              Get Free Quote <ArrowRight size={20} />
            </StyledButton>
            <StyledButton to="https://wa.me/447535935779" className="secondary">
              <MessageCircle size={20} /> WhatsApp Chat
            </StyledButton>
          </CTAButtons>
        </Container>
      </CTASection>

      <AnimatePresence>
        {lightboxImage && (
          <Lightbox
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={closeLightbox}
          >
            <button className="close-button" onClick={closeLightbox}>
              <X size={24} />
            </button>
            <div className="lightbox-content" onClick={(e) => e.stopPropagation()}>
              <img src={lightboxImage} alt={lightboxInfo?.title} />
              {lightboxInfo && (
                <div className="lightbox-info">
                  <h3>{lightboxInfo.title}</h3>
                  <p>{lightboxInfo.description}</p>
                </div>
              )}
            </div>
          </Lightbox>
        )}
      </AnimatePresence>
    </>
  );
};

export default GalleryPage;
